import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { HelmetProvider } from "react-helmet-async";
import { Suspense, lazy } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Toaster } from "@/components/ui/sonner";

// Code-split pages for performance
const HomePage = lazy(() => import("@/pages/HomePage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const QuotePage = lazy(() => import("@/pages/QuotePage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const OrdersPage = lazy(() => import("@/pages/OrdersPage"));
const SEODashboard = lazy(() => import("@/pages/SEODashboard"));
const AnalyticsDashboard = lazy(() => import("@/pages/AnalyticsDashboard"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FFFDF9' }}>
      <div className="ss-card p-8 text-center">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="font-bold text-slate-900">Loading...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<><Navbar /><HomePage /><Footer /></>} />
        <Route path="/about" element={<><Navbar /><AboutPage /><Footer /></>} />
        <Route path="/services" element={<><Navbar /><ServicesPage /><Footer /></>} />
        <Route path="/contact" element={<><Navbar /><ContactPage /><Footer /></>} />
        <Route path="/login" element={<><Navbar /><LoginPage /></>} />
        <Route path="/register" element={<><Navbar /><RegisterPage /></>} />
        <Route path="/quote" element={<><Navbar /><QuotePage /><Footer /></>} />
        <Route path="/dashboard" element={<ProtectedRoute><Navbar /><DashboardPage /><Footer /></ProtectedRoute>} />
        <Route path="/orders" element={<ProtectedRoute><Navbar /><OrdersPage /><Footer /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute adminOnly><Navbar /><AdminDashboard /><Footer /></ProtectedRoute>} />
        <Route path="/admin/seo" element={<ProtectedRoute adminOnly><Navbar /><SEODashboard /><Footer /></ProtectedRoute>} />
        <Route path="/admin/analytics" element={<ProtectedRoute adminOnly><Navbar /><AnalyticsDashboard /><Footer /></ProtectedRoute>} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
